/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import { TextValidator } from "react-material-ui-form-validator";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useEffect, useState } from "react";
import { AddressModel, CustomerModel } from "../../Utils/Models";
import { getData } from "../../Services/getData";
import { toast } from "react-toastify";
import { postData } from "../../Services/postData";

type ShippingAddressProps = {
  customer: CustomerModel;
  index: number;
  handleChangeCustomer: (product: CustomerModel) => void;
};
type StateType = {
  name: string;
  state_code: string;
};
export type CountryType = {
  name: string;
  iso3: string;
  iso2: string;
  states: Array<StateType>;
};
export default function Address(props: ShippingAddressProps) {
  const { customer, index, handleChangeCustomer } = props;
  const customerLocation = customer.locations?.[index];
  const [address, setAddress] = useState<AddressModel>(new AddressModel());
  const [countriesAndStates, setCountriesAndStates] = useState<
    Array<CountryType>
  >([]);
  const [cities, setCities] = useState<Array<string>>([]);
  const [shouldUpdate, setShouldUpdate] = useState<boolean>(false);

  useEffect(() => {
    getData("https://countriesnow.space/api/v0.1/countries/states")
      .then((res) => {
        const data = Array.from(
          new Map(
            res.data.data.map((item: CountryType) => [item.name, item])
          ).values()
        );
        setCountriesAndStates(data as CountryType[]);
      })
      .catch((err) => {
        toast.error("Cannot get countries");
      });
  }, []);

  /**
   *
   */
  useEffect(() => {
    if (customer && customerLocation) {
      setAddress(customerLocation.address);
    } else {
      setAddress(new AddressModel());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.locations?.[index]]);

  useEffect(() => {
    if (address.county) {
      postData("https://countriesnow.space/api/v0.1/countries/state/cities", {
        country: address.country,
        state: address.county,
      })
        .then((res) => {
          setCities(res.data.data);
        })
        .catch((error) => {
          toast.error("Cannot get cities");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.county]);

  useEffect(() => {
    if (shouldUpdate) {
      customerLocation.address = address;
      handleChangeCustomer(customer);
      setShouldUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdate]);

  const selectedCountry = countriesAndStates.find(
    (c: CountryType) => c.name === address.country
  );

  // Get the states for the selected country, or an empty array if none
  const countyOptions = selectedCountry?.states || [];

  if (!customerLocation) {
    return null;
  }

  return (
    <Grid container spacing={2} style={{ marginTop: 10 }}>
      {customer.isLegalPerson && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControl>
            <FormLabel id="clientTypeGroupLabel">
              {Vocabulary.addressType}
            </FormLabel>
            <RadioGroup
              onChange={(event) => {
                customerLocation.isWorkingPoint =
                  event.target.value === Vocabulary.workingPoint;
                setShouldUpdate(true);
              }}
              value={customer.isLegalPerson}
              aria-labelledby="clientTypeGroupLabel"
            >
              <FormControlLabel
                checked={customerLocation.isWorkingPoint}
                value={Vocabulary.workingPoint}
                control={<Radio />}
                label={Vocabulary.workingPoint}
              />
              <FormControlLabel
                checked={!customerLocation.isWorkingPoint}
                value={Vocabulary.office}
                control={<Radio />}
                label={Vocabulary.office}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <TextValidator
          size="small"
          id="details"
          name="details"
          label={Vocabulary.details}
          fullWidth
          variant="outlined"
          value={address.details}
          onChange={(event: any, value) => {
            setAddress({ ...address, details: event.target.value });
            setShouldUpdate(true);
          }}
          validators={["required"]}
          errorMessages={[Vocabulary.requiredField]}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <Autocomplete
          size="small"
          id="type"
          freeSolo={true}
          options={countriesAndStates.map(
            (country: CountryType) => country.name
          )}
          value={customerLocation.address.country}
          getOptionLabel={(option: any) => option}
          isOptionEqualToValue={(option: any, value: any) => option === value}
          getOptionDisabled={(option: string) => option === address?.country}
          onChange={(event, value: any) => {
            setAddress({ ...address, country: value, county: "" });
            setShouldUpdate(true);
          }}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.country}
              variant="outlined"
              value={address.country}
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <Autocomplete
          size="small"
          id="type"
          freeSolo={true}
          options={countyOptions.map((state) => state.name)}
          disabled={address.country === ""}
          value={address.county}
          getOptionLabel={(option: any) => option}
          isOptionEqualToValue={(option: any, value: any) => option === value}
          getOptionDisabled={(option: string) => option === address?.county}
          onChange={(event, value: any) => {
            setAddress({ ...address, county: value });
            setShouldUpdate(true);
          }}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.county}
              value={address.county}
              variant="outlined"
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <Autocomplete
          size="small"
          id="type"
          freeSolo={true}
          options={cities.map((state) => state)}
          disabled={address.county === ""}
          value={address.city}
          getOptionLabel={(option: any) => option}
          isOptionEqualToValue={(option: any, value: any) => option === value}
          getOptionDisabled={(option: string) => option === address?.city}
          onChange={(event, value: any) => {
            setAddress({ ...address, city: value });
            setShouldUpdate(true);
          }}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.city}
              value={address.city}
              variant="outlined"
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <TextValidator
          size="small"
          id="zip"
          name="zip"
          label={Vocabulary.zip}
          fullWidth
          variant="outlined"
          value={address.postcode}
          onChange={(event: any, value) => {
            setAddress({ ...address, postcode: event.target.value });
            setShouldUpdate(true);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <TextValidator
          size="small"
          id="bank"
          name="bank"
          label={Vocabulary.bank}
          fullWidth
          variant="outlined"
          value={customerLocation.bank}
          onChange={(event: any, value) => {
            customerLocation.bank = event.target.value;
            setShouldUpdate(true);
          }}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6}>
        <TextValidator
          size="small"
          id="iban"
          name="iban"
          label={Vocabulary.iban}
          fullWidth
          variant="outlined"
          value={customerLocation.iban}
          onChange={(event: any, value) => {
            customerLocation.iban = event.target.value;
            setShouldUpdate(true);
          }}
        />
      </Grid>
    </Grid>
  );
}
